import axios from 'axios';
import socketConfig from 'socket/socketio-client';
import { UNAUTHORIZED, INTERNAL_SERVER_ERROR } from 'http-status-codes';
import moment from 'moment';
import { globalApiErrorHandler } from './api-error-handler';
import {
  globalApiRequestValidator,
  getAxiosInstance,
} from './api-request-validator';
import { handleLoginResponse } from './auth-helper';
import { ROUTE_CONSTANTS } from 'constants/util/route-constants';
import { OMNIA_LINKING_UNLINKING_PROJECT_ID } from 'constants/feature/omnia-engagements-constants';
import { isNullOrUndefined } from 'utils/object-utils';
import { EMPTY_STRING } from 'constants/common/feature-common-utils';
import { createMSALInstance } from './msal-instance';
const LOGOUT_URL = '/logout';
/**
 * This is the first API request we make on any page, which gets the current user. This is used for
 * token handling with ADAL as well. This MUST be updated if we ever change the order of our API calls
 * throughout the application to be something else besides getting the current user to start.
 */
// const FIRST_API_REQUEST_PATH = '/tieout/currentuser';

let apiClient = {};

Object.keys(window?.TIEOUT?.ENV.GEOS || {}).forEach((geoKey) => {
  apiClient[geoKey] = {
    api: null,
    flags: {
      authenticated: false,
    },
  };
});

export const undeterminedGeoKey = 'undetermined-geoKey';

export const createAuthContext = (geoKey) => {
  const apiClient = {};
  createMSALInstance();
  apiClient[geoKey] = getAPIClient(geoKey);
  apiClient[geoKey].socketURL = window.TIEOUT.ENV.GEOS[geoKey].BASE_HUB_URL;

  apiClient[geoKey].api.interceptors.request.use(
    async (config) => {
      const isLogoutAPI = config.url === LOGOUT_URL;
      const token = await _getAuthTokenForRequestInterceptors(
        geoKey,
        isLogoutAPI,
      );
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['localDate'] = moment();
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  /**
   * Here we are constructing an interceptor for catching certain response exceptions and handling the refresh of oauth tokens based
   * on response error codes.
   */
  apiClient[geoKey].api.interceptors.response.use(undefined, (err) => {
    const { response } = err;
    /**
     * This boolean is used to track that the user has not submitted multiple requests to the application
     * as receiving the same error twice for the same api call implies there is a problem with their session from our
     * authentication provider or that they are trying to brute force our authentication setup.
     *  */
    const _isValidResponseAndNotRetry =
      response &&
      response.config &&
      typeof response.config.url === 'string' &&
      !response.config.__isRetryRequest;

    /**
     * This case covers when we have an expired token and try to make an API request. In this case, we receive
     * a 401 - UNAUTHORIZED response. We have to refresh our token in this case for further API requests to work.
     */
    const _tokenTimeOutResponse =
      _isValidResponseAndNotRetry && response.status === UNAUTHORIZED;

    /**
     * This final case covers when a user has been idle on their computer for an incredibly long time (more than a few hours).
     * In this case we receive a 500 - INTERNAL_SERVER_ERROR response with a very specific socket timeout exception message.
     * This specific error case is a trigger for us to refresh our authorization token.
     */
    const _socketTimeOutResponse =
      _isValidResponseAndNotRetry &&
      response.status === INTERNAL_SERVER_ERROR &&
      response.data &&
      response.data.rootCause === 'SocketTimeoutException: connect timed out';

    /**
     * These cases above are the only error response cases in which we should obtain or refresh our authentication token.
     * In every other case we should allow the request (and its response) to fail.
     */
    const _shouldAcquireToken =
      // _emptyTokenResponseForFirstRequest ||
      _tokenTimeOutResponse || _socketTimeOutResponse;

    if (_shouldAcquireToken) {
      return _acquireToken(err, response, apiClient[geoKey], geoKey);
    } else {
      return Promise.reject(err);
    }
  });
  return apiClient[geoKey];
};

/**
 * A helper function used to obtain or refresh the token from ADAL used in our application using an implicit-grant oauth flow.
 * @param {Object} err : The error response wrapper that we receive from the back-end when we lack a token, or send an invalid one.
 * @param {Object} response: The HTTP Response object which contains things like status codes, error messages, etc... from the API.
 * @param {Object} apiClient: The api client wrapper that we use to retain tokens and track other responses from the back-end.
 */
const _acquireToken = (err, response, apiClient, geoKey) => {
  response.config.__isRetryRequest = true;
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    const tokenResponse = await handleLoginResponse(geoKey);
    const token = tokenResponse.accessToken;
    if (!token) {
      return;
    }

    // eslint-disable-next-line require-atomic-updates
    apiClient.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    // eslint-disable-next-line require-atomic-updates
    apiClient.flags.authenticated = true;
    // eslint-disable-next-line require-atomic-updates
    apiClient.token = token;
    err.config.headers.Authorization = `Bearer ${token}`;
    socketConfig.autoConnect = true;
    axios(err.config).then(resolve, reject);
  });
};

export const _getAuthTokenForRequestInterceptors = async (
  geoKey,
  isLogoutAPI = false,
) => {
  const tokenResponse = await handleLoginResponse(geoKey, isLogoutAPI);

  socketConfig.autoConnect = true;
  return tokenResponse.accessToken;
};

export const setProjectHeadersAction = (config, projectId) => {
  const pathName = window.location.pathname;
  let defaultConfig = config && config.defaults;

  const isCreateProjectPage =
    pathName && pathName.includes(ROUTE_CONSTANTS.PROJECT_CREATION_PAGE);

  const isAdminModulePage =
    pathName && pathName.includes(ROUTE_CONSTANTS.ADMIN_MODULE_PAGE);
  // get updated selected project data (it has to be used to set http header)
  const selectedProjectID = sessionStorage.getItem('selected_project_id');

  const isOmniaPage =
    pathName && pathName.includes(ROUTE_CONSTANTS.OMNIA_ENGAGEMENT_PAGE);
  const omniaProjectId = localStorage.getItem(
    OMNIA_LINKING_UNLINKING_PROJECT_ID,
  );
  // after loading data to redux store update country code and member firm code to be sent with http headers.

  if (defaultConfig) {
    if (isOmniaPage) {
      defaultConfig.headers.common['x-client-id'] = isNullOrUndefined(
        omniaProjectId,
      )
        ? parseInt(selectedProjectID)
        : parseInt(omniaProjectId);
    } else if (isAdminModulePage) {
      // adding client id to header when in admin module page
      defaultConfig.headers.common['x-client-id'] = 0;
    } else if (isCreateProjectPage) {
      // adding client id to header when in create project page
      defaultConfig.headers.common['x-client-id'] = -1;
    } else if (projectId) {
      defaultConfig.headers.common['x-client-id'] = projectId;
    } else if (selectedProjectID) {
      // adding client id to header
      defaultConfig.headers.common['x-client-id'] = isNullOrUndefined(
        selectedProjectID,
      )
        ? EMPTY_STRING
        : parseInt(selectedProjectID);

      if (defaultConfig.headers.common['x-client-id'] === EMPTY_STRING) {
        //try to get project id from url
        const isStatemenmtPage = pathName.includes('statement');
        if (isStatemenmtPage) {
          const urlSections = pathName.split('/');
          const projectIdIndex = urlSections.indexOf('project') + 1;
          defaultConfig.headers.common['x-client-id'] = parseInt(
            urlSections[projectIdIndex],
          );
        }
      }
    }
  }
};
/**
 * The axiosInstance setup by this private function is used throughout the application to manage API calls to the back-end, using environment configured parameters.
 * Going forward we will be using this as an api client wrapper for all of our axios requests, saving us from having to set headers,
 * manage api urls, etc... throughout the API files. This setup will also likely change
 * to a collection of axios api constants referenced by region once we need to handle globalization in TieOut as well.
 *
 * TODO: Setup array of api variables for handling globalization.
 *
 * Note: This function should not be called externally, instead, retrieve the api through the getAPI function below.
 */
const _createAPI = (geoKey) => {
  /** axios instace for invalid geoKey */
  if (geoKey === undeterminedGeoKey) {
    console.warn('geoKey could not be determined for the project id');
    apiClient[`${undeterminedGeoKey}`] = getAxiosInstance();
    globalApiRequestValidator(
      undeterminedGeoKey,
      apiClient[undeterminedGeoKey],
    );
    return;
  }
  /** axios instace for valid geoKey */
  apiClient[geoKey].api = axios.create({
    baseURL: window.TIEOUT.ENV.GEOS[geoKey].BASE_API_URL,
    /* Add all custom API settings we will need here. */
  });
  createAuthContext(geoKey);
  globalApiErrorHandler(apiClient[geoKey]);
  globalApiRequestValidator(geoKey, apiClient[geoKey]);
};

export const getAPIClient = (geoKey) => {
  if (!(apiClient[geoKey] && apiClient[geoKey].api)) {
    _createAPI(geoKey);
  }
  return apiClient[geoKey];
};

/**
 * A public getter function to fetch the reference to the api elsewhere in the application. If no api reference has been intialized
 * yet, this function calls the `_createAPI()` function to create one.
 */
export const getAPI = (geoKey, projectId) => {
  if (!(apiClient[geoKey] && apiClient[geoKey].api)) {
    _createAPI(geoKey);
  }
  // set header (related to client id, container code, member firm code) before each api call
  setProjectHeadersAction(apiClient[geoKey].api, projectId);
  return apiClient[geoKey].api;
};
